/**
 * React Query hooks for the Product Map (`apps/api/src/product-map/routes.ts`).
 *
 * Follows `features/planning/usePlans.ts` for the request/query-key
 * conventions, and `features/product-board/hooks/useProductItems.ts` for the
 * capability-move optimistic update (via `productMapLogic.ts` +
 * `boards/optimisticMove.ts`, since the product-map cache is one nested
 * per-property object rather than one flat array per query).
 *
 * @module features/product-map/useProductMap
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductMapImportance, ProductMapStatus } from '@chatsdet/types';
import { API_URL } from '../../config';
import { parseApiJson } from '../../lib/api-error';
import { rollbackMove, spliceIntoList, type MoveSnapshot } from '../boards/optimisticMove';
import {
  applyOptimisticAreaArchive,
  applyOptimisticAreaReorder,
  applyOptimisticCapabilityArchive,
  applyOptimisticCapabilityMove,
} from './productMapLogic';
import type {
  CapabilityRuleRecord,
  MoveCapabilityInput,
  ProductArea,
  ProductCapability,
  ProductCapabilityDetail,
  ProductCoverageResponse,
  ProductMapResponse,
  WorkflowCapabilityOption,
} from './types';

const productMapKeys = {
  all: ['product-map'] as const,
  map: (propertyId: string | undefined) => ['product-map', 'map', propertyId] as const,
  capability: (id: string | undefined) => ['product-map', 'capability', id] as const,
  coverage: (days: number, propertyId: string | undefined) =>
    ['product-map', 'coverage', days, propertyId] as const,
  workflowCapabilities: (workflowId: string | undefined) =>
    ['product-map', 'workflow-capabilities', workflowId] as const,
};

async function productMapRequest<T>(path: string, init?: RequestInit, fallback?: string): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: init?.body ? { 'content-type': 'application/json', ...init.headers } : init?.headers,
  });
  return parseApiJson<T>(response, fallback ?? 'Product map request failed');
}

export function useInvalidateProductMap() {
  const queryClient = useQueryClient();
  // Memoized because the assistant's capability-invalidation effect depends on
  // this identity; an unstable one re-ran that effect — and its rescan of the
  // whole message list — on every render during token streaming.
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: productMapKeys.all }),
    [queryClient]
  );
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useProductMap(propertyId: string | undefined) {
  return useQuery({
    queryKey: productMapKeys.map(propertyId),
    queryFn: () =>
      productMapRequest<{ ok: true; data: ProductMapResponse }>(
        `/product-map?propertyId=${encodeURIComponent(propertyId!)}`,
        undefined,
        'Failed to load the product map'
      ).then((res) => res.data),
    enabled: Boolean(propertyId),
  });
}

export function useCapabilityDetail(capabilityId: string | undefined) {
  return useQuery({
    queryKey: productMapKeys.capability(capabilityId),
    queryFn: () =>
      productMapRequest<{ ok: true; data: ProductCapabilityDetail }>(
        `/product-map/capabilities/${capabilityId}`,
        undefined,
        'Failed to load capability'
      ).then((res) => res.data),
    enabled: Boolean(capabilityId),
  });
}

/**
 * Coverage summary + 30-day history (`GET /product-map/coverage`). Omit
 * `propertyId` for the org-wide dashboard card; pass it to also receive
 * `current.byArea` for a property's product-map header.
 */
export function useProductCoverage(days = 30, propertyId?: string) {
  return useQuery({
    queryKey: productMapKeys.coverage(days, propertyId),
    queryFn: () =>
      productMapRequest<{ ok: true; data: ProductCoverageResponse }>(
        `/product-map/coverage?days=${days}${propertyId ? `&propertyId=${encodeURIComponent(propertyId)}` : ''}`,
        undefined,
        'Failed to load product coverage'
      ).then((res) => res.data),
  });
}

// ---------------------------------------------------------------------------
// Areas
// ---------------------------------------------------------------------------

export function useCreateArea(propertyId: string) {
  const invalidate = useInvalidateProductMap();
  return useMutation({
    mutationFn: (input: { title: string; description?: string | null }) =>
      productMapRequest<{ ok: true; data: ProductArea }>(
        '/product-map/areas',
        { method: 'POST', body: JSON.stringify({ propertyId, ...input }) },
        'Failed to create area'
      ).then((res) => res.data),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateArea() {
  const invalidate = useInvalidateProductMap();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      title?: string;
      description?: string | null;
      status?: ProductMapStatus;
    }) =>
      productMapRequest<{ ok: true; data: ProductArea }>(
        `/product-map/areas/${id}`,
        { method: 'PATCH', body: JSON.stringify(body) },
        'Failed to update area'
      ).then((res) => res.data),
    onSuccess: () => invalidate(),
  });
}

/**
 * Deprecates (soft-deletes) an area — it keeps its code, but drops off the
 * active map. The server refuses (409 `PRODUCT_MAP_STAGE_ORDER`) while the
 * area still has live capabilities; the optimistic write is rolled back then.
 */
export function useDeprecateArea(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      productMapRequest<{ ok: true }>(
        `/product-map/areas/${id}`,
        { method: 'DELETE' },
        'Failed to deprecate area'
      ),
    onMutate: async (id: string) => {
      const key = productMapKeys.map(propertyId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProductMapResponse>(key);
      if (previous) {
        queryClient.setQueryData<ProductMapResponse>(key, applyOptimisticAreaArchive(previous, id));
      }
      return { snapshot: previous ? ([[key, previous]] as MoveSnapshot) : undefined };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) rollbackMove(queryClient, ctx.snapshot);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: productMapKeys.all }),
  });
}

export function useReorderAreas(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      productMapRequest<{ ok: true }>(
        '/product-map/areas/reorder',
        { method: 'POST', body: JSON.stringify({ orderedIds }) },
        'Failed to reorder areas'
      ),
    onMutate: async (orderedIds: string[]) => {
      const key = productMapKeys.map(propertyId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProductMapResponse>(key);
      if (previous) {
        queryClient.setQueryData<ProductMapResponse>(key, applyOptimisticAreaReorder(previous, orderedIds));
      }
      return { snapshot: previous ? ([[key, previous]] as MoveSnapshot) : undefined };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) rollbackMove(queryClient, ctx.snapshot);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: productMapKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

export function useCreateCapability(propertyId: string) {
  const invalidate = useInvalidateProductMap();
  return useMutation({
    mutationFn: (input: {
      areaId?: string | null;
      title: string;
      description?: string | null;
      bodyMd?: string | null;
      importance?: ProductMapImportance;
    }) =>
      productMapRequest<{ ok: true; data: ProductCapability }>(
        '/product-map/capabilities',
        { method: 'POST', body: JSON.stringify({ propertyId, ...input }) },
        'Failed to create capability'
      ).then((res) => res.data),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateCapability() {
  const invalidate = useInvalidateProductMap();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      title?: string;
      description?: string | null;
      bodyMd?: string | null;
      importance?: ProductMapImportance;
      status?: ProductMapStatus;
      entryUrlPattern?: string | null;
      setupMd?: string | null;
      openQuestionsMd?: string | null;
      blockedReason?: string | null;
    }) =>
      productMapRequest<{ ok: true; data: ProductCapability }>(
        `/product-map/capabilities/${id}`,
        { method: 'PATCH', body: JSON.stringify(body) },
        'Failed to update capability'
      ).then((res) => res.data),
    onSuccess: () => invalidate(),
  });
}

export function useMoveCapability(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MoveCapabilityInput) =>
      productMapRequest<{ ok: true; data: ProductCapability }>(
        `/product-map/capabilities/${input.id}/move`,
        {
          method: 'POST',
          body: JSON.stringify({
            areaId: input.areaId,
            beforeCapabilityId: input.beforeCapabilityId,
            afterCapabilityId: input.afterCapabilityId,
          }),
        },
        'Failed to move capability'
      ).then((res) => res.data),
    onMutate: async (input: MoveCapabilityInput) => {
      const key = productMapKeys.map(propertyId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProductMapResponse>(key);
      if (previous) {
        const next = applyOptimisticCapabilityMove(previous, input, spliceIntoList);
        if (next) queryClient.setQueryData<ProductMapResponse>(key, next);
      }
      return { snapshot: previous ? ([[key, previous]] as MoveSnapshot) : undefined };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) rollbackMove(queryClient, ctx.snapshot);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: productMapKeys.all }),
  });
}

/** Deprecates (soft-deletes) a capability. Removes it wherever it renders — no reparenting involved. */
export function useDeprecateCapability(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      productMapRequest<{ ok: true }>(
        `/product-map/capabilities/${id}`,
        { method: 'DELETE' },
        'Failed to deprecate capability'
      ),
    onMutate: async (id: string) => {
      const key = productMapKeys.map(propertyId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProductMapResponse>(key);
      if (previous) {
        queryClient.setQueryData<ProductMapResponse>(key, applyOptimisticCapabilityArchive(previous, id));
      }
      return { snapshot: previous ? ([[key, previous]] as MoveSnapshot) : undefined };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) rollbackMove(queryClient, ctx.snapshot);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: productMapKeys.all }),
  });
}

/**
 * Full-replace the set of workflows linked to a capability
 * (`PUT /product-map/capabilities/:id/workflows`). Invalidates both the
 * capability detail (its `activeWorkflowIds`/`activeLinkCount`) and the map
 * query (area/property roll-ups), same as `useAcceptProposal`.
 */
export function useSetCapabilityWorkflows(capabilityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workflowIds: string[]) =>
      productMapRequest<{
        ok: true;
        data: { inserted: string[]; unsuppressed: string[]; tombstoned: string[] };
      }>(
        `/product-map/capabilities/${capabilityId}/workflows`,
        { method: 'PUT', body: JSON.stringify({ workflowIds }) },
        'Failed to update linked flows'
      ).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productMapKeys.capability(capabilityId) });
      queryClient.invalidateQueries({ queryKey: productMapKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Capability rules
// ---------------------------------------------------------------------------

/** Adds a rule to a capability (`POST /product-map/capabilities/:id/rules`). */
export function useAddRule(capabilityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (statement: string) =>
      productMapRequest<{ ok: true; data: CapabilityRuleRecord }>(
        `/product-map/capabilities/${capabilityId}/rules`,
        { method: 'POST', body: JSON.stringify({ statement }) },
        'Failed to add rule'
      ).then((res) => res.data),
    // CapabilityRulesSection renders addRule.error inline under the add row;
    // suppress the global toast so duplicate-rule errors aren't shown twice.
    meta: { suppressToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productMapKeys.capability(capabilityId) });
      queryClient.invalidateQueries({ queryKey: productMapKeys.all });
    },
  });
}

/** Edits a rule's statement (`PATCH /product-map/rules/:id`). */
export function useUpdateRule(capabilityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; statement?: string; status?: ProductMapStatus }) =>
      productMapRequest<{ ok: true; data: CapabilityRuleRecord }>(
        `/product-map/rules/${id}`,
        { method: 'PATCH', body: JSON.stringify(body) },
        'Failed to update rule'
      ).then((res) => res.data),
    // RuleRow renders the update error inline next to the edited row; suppress
    // the global toast so duplicate-rule errors aren't shown twice.
    meta: { suppressToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productMapKeys.capability(capabilityId) });
      queryClient.invalidateQueries({ queryKey: productMapKeys.all });
    },
  });
}

/** Deprecates (soft-deletes) a rule (`DELETE /product-map/rules/:id`). */
export function useRemoveRule(capabilityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      productMapRequest<{ ok: true }>(`/product-map/rules/${id}`, { method: 'DELETE' }, 'Failed to remove rule'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productMapKeys.capability(capabilityId) });
      queryClient.invalidateQueries({ queryKey: productMapKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Workflow-side capability links (flow settings)
// ---------------------------------------------------------------------------

export function useWorkflowCapabilities(workflowId: string | undefined) {
  return useQuery({
    queryKey: productMapKeys.workflowCapabilities(workflowId),
    queryFn: () =>
      productMapRequest<{ ok: true; data: { capabilities: WorkflowCapabilityOption[] } }>(
        `/product-map/workflows/${workflowId}/capabilities`,
        undefined,
        'Failed to load linked capabilities'
      ).then((res) => res.data.capabilities),
    enabled: Boolean(workflowId),
  });
}

/**
 * Full-replace the set of capabilities linked to a workflow
 * (`PUT /product-map/workflows/:id/capabilities`).
 */
export function useSetWorkflowCapabilities(workflowId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (capabilityIds: string[]) =>
      productMapRequest<{
        ok: true;
        data: { inserted: string[]; unsuppressed: string[]; tombstoned: string[] };
      }>(
        `/product-map/workflows/${workflowId}/capabilities`,
        { method: 'PUT', body: JSON.stringify({ capabilityIds }) },
        'Failed to update capability links'
      ).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productMapKeys.workflowCapabilities(workflowId) });
      queryClient.invalidateQueries({ queryKey: productMapKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Proposals
// ---------------------------------------------------------------------------

export function useAcceptProposal() {
  const invalidate = useInvalidateProductMap();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ revisionId }: { revisionId: string; capabilityId?: string }) =>
      productMapRequest<{ ok: true; data: unknown }>(
        `/product-map/proposals/${revisionId}/accept`,
        { method: 'POST' },
        'Failed to apply planner suggestion'
      ),
    onSuccess: (_data, vars) => {
      if (vars.capabilityId) {
        queryClient.invalidateQueries({ queryKey: productMapKeys.capability(vars.capabilityId) });
      }
      invalidate();
    },
  });
}

export { productMapKeys };
